import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { PlusCircle, Zap, LogOut, User, Swords } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const linkBase =
  "px-4 py-2 rounded text-sm font-semibold transition-all";
const linkInactive = "text-[#A3A3A3] hover:text-white";
const linkActive = "bg-[#FF3B30] text-white shadow-md";

export default function Layout({ onOpenLogModal }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col font-sans">
      {/* NAVBAR */}
      <header className="sticky top-0 z-50 bg-[#141414]/85 backdrop-blur-xl border-b border-[#262626] px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
        <button
          data-testid="brand-home"
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 sm:gap-3 group flex-shrink-0"
        >
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded bg-[#FF3B30] flex items-center justify-center text-white font-black text-lg sm:text-xl shadow-[0_0_15px_rgba(255,59,48,0.4)] group-hover:scale-105 transition-transform">
            CP
          </div>
          <div className="text-left hidden sm:block">
            <h1 className="text-xl font-black uppercase tracking-wider text-white">
              CRICKETPULSE
            </h1>
            <p className="text-xs text-[#A3A3A3] tracking-widest uppercase">
              Performance Analytics Pro
            </p>
          </div>
        </button>

        <nav className="hidden md:flex items-center gap-1 bg-[#1A1A1A] p-1.5 rounded-lg border border-[#262626]">
          <NavLink
            data-testid="nav-dashboard"
            to="/dashboard"
            className={({ isActive }) =>
              `${linkBase} ${isActive ? linkActive : linkInactive}`
            }
          >
            Dashboard
          </NavLink>
          <NavLink
            data-testid="nav-matches"
            to="/matches"
            className={({ isActive }) =>
              `${linkBase} ${isActive ? linkActive : linkInactive}`
            }
          >
            Match Logs
          </NavLink>
          <NavLink
            data-testid="nav-analytics"
            to="/analytics"
            className={({ isActive }) =>
              `${linkBase} ${isActive ? linkActive : linkInactive}`
            }
          >
            Advanced Analytics
          </NavLink>
          <NavLink
            data-testid="nav-aicoach"
            to="/ai-coach"
            className={({ isActive }) =>
              `${linkBase} flex items-center gap-1.5 ${isActive ? linkActive : linkInactive}`
            }
          >
            <Zap className="w-4 h-4 text-yellow-400" /> AI Coach
          </NavLink>
          <NavLink
            data-testid="nav-compare"
            to="/compare"
            className={({ isActive }) =>
              `${linkBase} flex items-center gap-1.5 ${isActive ? linkActive : linkInactive}`
            }
          >
            <Swords className="w-4 h-4 text-[#FF3B30]" /> Compare
          </NavLink>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <button
            data-testid="log-match-trigger-btn"
            onClick={onOpenLogModal}
            className="bg-[#FF3B30] hover:bg-[#E03229] text-white px-3 sm:px-4 py-2 rounded text-xs sm:text-sm font-bold flex items-center gap-1.5 sm:gap-2 shadow-[0_4px_20px_rgba(255,59,48,0.3)] transition-all hover:-translate-y-0.5"
          >
            <PlusCircle className="w-4 h-4" /> <span className="hidden sm:inline">Log Match</span><span className="sm:hidden">Log</span>
          </button>

          <button
            data-testid="nav-profile-btn"
            onClick={() => navigate("/profile")}
            className="hidden sm:flex items-center gap-2 bg-[#1A1A1A] border border-[#262626] px-3 py-1.5 rounded-lg hover:border-[#FF3B30]/50 transition-all"
          >
            <div className="w-7 h-7 rounded-full bg-[#FF3B30] flex items-center justify-center text-white text-xs font-black uppercase">
              {(user?.name || user?.email || "P").slice(0, 2)}
            </div>
            <div className="hidden lg:block text-left">
              <p className="text-xs font-bold text-white leading-tight">{user?.name}</p>
              <p className="text-[10px] text-emerald-400 font-mono">
                {user?.profile?.playing_role || "Player"}
              </p>
            </div>
          </button>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="p-2 rounded bg-[#1A1A1A] border border-[#262626] text-[#A3A3A3] hover:text-[#FF3B30] hover:border-[#FF3B30]/50"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* MOBILE NAV */}
      <div className="md:hidden flex items-center justify-around bg-[#141414] border-b border-[#262626] py-2 px-2 sticky top-[57px] z-40 flex-wrap gap-1">
        <NavLink to="/dashboard" className={({ isActive }) => `text-[11px] font-semibold py-1 px-2 rounded ${isActive ? "bg-[#FF3B30] text-white" : "text-[#A3A3A3]"}`}>Dashboard</NavLink>
        <NavLink to="/matches" className={({ isActive }) => `text-[11px] font-semibold py-1 px-2 rounded ${isActive ? "bg-[#FF3B30] text-white" : "text-[#A3A3A3]"}`}>Logs</NavLink>
        <NavLink to="/analytics" className={({ isActive }) => `text-[11px] font-semibold py-1 px-2 rounded ${isActive ? "bg-[#FF3B30] text-white" : "text-[#A3A3A3]"}`}>Analytics</NavLink>
        <NavLink to="/ai-coach" className={({ isActive }) => `text-[11px] font-semibold py-1 px-2 rounded flex items-center gap-1 ${isActive ? "bg-[#FF3B30] text-white" : "text-[#A3A3A3]"}`}><Zap className="w-3 h-3 text-yellow-400" /> AI</NavLink>
        <NavLink to="/compare" className={({ isActive }) => `text-[11px] font-semibold py-1 px-2 rounded ${isActive ? "bg-[#FF3B30] text-white" : "text-[#A3A3A3]"}`}>Compare</NavLink>
        <NavLink to="/profile" className={({ isActive }) => `text-[11px] font-semibold py-1 px-2 rounded ${isActive ? "bg-[#FF3B30] text-white" : "text-[#A3A3A3]"}`}>Profile</NavLink>
      </div>

      <main className="flex-1 max-w-7xl w-full mx-auto p-6 md:p-8 space-y-8">
        <Outlet />
      </main>

      <footer className="bg-[#141414] border-t border-[#262626] py-10 px-6 mt-16 text-center text-xs text-[#A3A3A3] space-y-2">
        <div className="flex items-center justify-center gap-2">
          <div className="w-6 h-6 rounded bg-[#FF3B30] flex items-center justify-center text-white font-black text-xs">CP</div>
          <span className="font-bold text-white uppercase tracking-wider">CricketPulse Analytics</span>
        </div>
        <p>Professional Batting & Bowling Intelligence • Powered by Gemini 3.6 Flash</p>
        <p className="font-mono text-[10px]">© 2026 CricketPulse Systems.</p>
      </footer>
    </div>
  );
}
